import { useState } from 'react';
import AdminSidebar from '@/components/AdminSidebar';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { Menu, Upload, Trash2, FolderPlus, Search, Image as ImageIcon, Eye, FolderOpen, Loader2 } from 'lucide-react';
import { useQuery, useQueryClient } from 'react-query';
import { getMedia, searchMedia, uploadMedia, deleteMedia, bulkDeleteMedia, getFolders, createFolder, type CmsMedia, type MediaFolder } from '@/api/mediaService';
import { useToast } from '@/components/ui/use-toast';

export default function CmsMediaLibrary() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);

  const { data: folders = [] } = useQuery('media-folders', getFolders);
  const { data: mediaItems = [], isLoading } = useQuery(
    ['media', activeFolderId, searchQuery],
    () => searchQuery ? searchMedia(searchQuery) : getMedia(activeFolderId),
    { keepPreviousData: true }
  );

  const handleUpload = async (files: FileList) => {
    setUploading(true);
    try {
      for (const file of Array.from(files)) { await uploadMedia(file, activeFolderId); }
      queryClient.invalidateQueries('media');
      toast({ title: `${files.length} bestand(en) geupload` });
    } catch (err) { toast({ variant: 'destructive', title: 'Upload mislukt', description: String(err) }); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Bestand verwijderen?')) return;
    try {
      await deleteMedia(id);
      queryClient.invalidateQueries('media');
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    } catch (err) { toast({ variant: 'destructive', title: 'Verwijderen mislukt', description: String(err) }); }
  };

  const handleBulkDelete = async () => {
    if (!confirm(`${selectedIds.size} bestanden verwijderen?`)) return;
    try {
      await bulkDeleteMedia(Array.from(selectedIds));
      queryClient.invalidateQueries('media');
      setSelectedIds(new Set());
    } catch (err) { toast({ variant: 'destructive', title: 'Verwijderen mislukt', description: String(err) }); }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await createFolder(newFolderName.trim(), activeFolderId);
      queryClient.invalidateQueries('media-folders');
      setNewFolderName('');
      setShowNewFolder(false);
    } catch (err) { toast({ variant: 'destructive', title: 'Map aanmaken mislukt', description: String(err) }); }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AdminSidebar />
        <main className="flex-1 overflow-auto bg-[#f6f6f7]">
          <div className="border-b border-gray-200 bg-white px-8 py-6">
            <div className="max-w-6xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-3">
                <SidebarTrigger className="md:hidden"><Menu className="h-5 w-5" /></SidebarTrigger>
                <div>
                  <h1 className="text-xl font-semibold text-[#202223]">Media</h1>
                  <p className="text-sm text-[#6d7175] mt-0.5">Upload en beheer je bestanden</p>
                </div>
              </div>
              <label className="inline-flex items-center gap-2 bg-[#202223] text-white px-4 py-2 rounded-lg text-[13px] font-medium cursor-pointer hover:bg-[#333] transition-colors">
                <Upload className="h-4 w-4" />
                {uploading ? 'Uploaden...' : 'Bestanden uploaden'}
                <input type="file" multiple accept="image/*,video/*" onChange={(e) => e.target.files && handleUpload(e.target.files)} className="hidden" />
              </label>
            </div>
          </div>

          <div className="px-8 py-8">
            <div className="max-w-6xl mx-auto flex gap-6">
              {/* Folder sidebar */}
              <div className="hidden md:block w-52 shrink-0">
                <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] border border-gray-200/60 p-3">
                  <div className="flex items-center justify-between mb-2 px-1">
                    <span className="text-[13px] font-semibold text-[#202223]">Mappen</span>
                    <button onClick={() => setShowNewFolder(!showNewFolder)} className="text-gray-400 hover:text-[#202223] p-0.5">
                      <FolderPlus className="h-4 w-4" />
                    </button>
                  </div>
                  {showNewFolder && (
                    <input type="text" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()} placeholder="Mapnaam..." className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 mb-2 outline-none focus:border-gray-300" autoFocus />
                  )}
                  <button onClick={() => { setActiveFolderId(null); setSearchQuery(''); }} className={`w-full text-left text-[13px] px-2.5 py-2 rounded-lg mb-0.5 transition-colors ${activeFolderId === null && !searchQuery ? 'bg-gray-100 text-[#202223] font-medium' : 'text-[#6d7175] hover:bg-gray-50'}`}>
                    Alle bestanden
                  </button>
                  {folders.map((folder) => (
                    <button key={folder.id} onClick={() => { setActiveFolderId(folder.id); setSearchQuery(''); }} className={`w-full text-left text-[13px] px-2.5 py-2 rounded-lg mb-0.5 flex items-center gap-2 transition-colors ${activeFolderId === folder.id ? 'bg-gray-100 text-[#202223] font-medium' : 'text-[#6d7175] hover:bg-gray-50'}`}>
                      <FolderOpen className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{folder.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Main content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-5">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Zoek bestanden..." className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-gray-300 bg-white" />
                  </div>
                  {selectedIds.size > 0 && (
                    <button onClick={handleBulkDelete} className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg text-[13px] font-medium hover:bg-red-700">
                      <Trash2 className="h-4 w-4" /> {selectedIds.size} verwijderen
                    </button>
                  )}
                </div>

                {isLoading ? (
                  <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
                ) : mediaItems.length === 0 ? (
                  <div className="text-center py-20">
                    <ImageIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-[#6d7175] text-sm">Geen bestanden gevonden</p>
                    <p className="text-xs text-gray-400 mt-1">Upload bestanden om te beginnen</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                    {mediaItems.map((item) => (
                      <div
                        key={item.id}
                        className={`group relative bg-white rounded-xl border overflow-hidden cursor-pointer transition-all ${selectedIds.has(item.id) ? 'border-[#202223] ring-1 ring-[#202223]/20' : 'border-gray-200/60 hover:border-gray-300 shadow-[0_1px_3px_rgba(0,0,0,0.04)]'}`}
                        onClick={() => toggleSelect(item.id)}
                      >
                        <div className="aspect-square bg-gray-50">
                          <img src={item.fileUrl} alt={item.altText || item.originalName} className="w-full h-full object-cover" />
                        </div>
                        <div className="px-2.5 py-2">
                          <p className="text-xs text-[#202223] truncate font-medium">{item.originalName}</p>
                          <p className="text-[11px] text-[#6d7175]">{(item.fileSize / 1024).toFixed(0)} KB</p>
                        </div>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                          <button onClick={(e) => { e.stopPropagation(); window.open(item.fileUrl, '_blank'); }} className="bg-white p-1.5 rounded-lg shadow-sm border border-gray-200 hover:bg-gray-50">
                            <Eye className="h-3.5 w-3.5 text-[#6d7175]" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} className="bg-white p-1.5 rounded-lg shadow-sm border border-gray-200 hover:bg-red-50">
                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
